import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

interface EmptyStateProps {
  icon: LucideIcon;
  secondaryIcon?: LucideIcon;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  compact?: boolean;
  className?: string;
}

export function EmptyState({
  icon: Icon,
  secondaryIcon: SecondaryIcon,
  title,
  description,
  actionLabel,
  onAction,
  compact = false,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'p-8' : 'p-12 sm:p-16',
        className,
      )}
    >
      {/* Icon container with gradient background */}
      <div className="relative mb-6">
        <div className="bg-primary/10 flex h-20 w-20 items-center justify-center rounded-2xl sm:h-24 sm:w-24">
          <Icon className="text-primary h-10 w-10 sm:h-12 sm:w-12" />
        </div>
        {SecondaryIcon && (
          <div className="gradient-accent absolute -right-2 -bottom-2 flex h-10 w-10 items-center justify-center rounded-xl shadow-lg">
            <SecondaryIcon className="h-5 w-5 text-white" />
          </div>
        )}
      </div>

      {/* Text content */}
      <h3 className="text-foreground mb-2 text-lg font-semibold sm:text-xl">{title}</h3>
      <p className="text-muted-foreground max-w-sm text-sm leading-relaxed sm:text-base">{description}</p>

      {/* Action button */}
      {actionLabel && onAction && (
        <Button onClick={onAction} className="gradient-primary mt-6 h-11 px-6 transition-opacity hover:opacity-90">
          {actionLabel}
        </Button>
      )}
    </div>
  );
}
